import { useCallback, useMemo } from 'react'
import { CalendarClock } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, FetchResult } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { api } from '@/lib/api'

// A16.41 — Rejalashtirilgan hisobotlar. Modul-lokal (adapters.ts / i18n / screenStatus
// HOT — tegilmaydi). Backend `/admin/analytics/scheduled-reports` greenfield → fetcher
// fail-soft bo'sh ro'yxatga tushadi; endpoint landanda jonli to'ladi (devApi pattern).
// DataTable turdagi ustunlar bilan real UI — dormant lekin funksional.

/** A16.41 — rejalashtirilgan hisobot yozuvi. */
export interface ScheduledReportRow {
  id: number
  name: string
  /** Cron yoki inson-o'qiy jadval (masalan «Har kuni 08:00»). */
  schedule: string
  format: 'csv' | 'xlsx' | 'pdf' | 'json'
  recipients: string
  last_run: string | null
  next_run: string | null
  status: 'active' | 'paused' | 'failed'
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

function toResult<T>(rows: T[], params: FetchParams, search: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

function fetchScheduledReports(params: FetchParams): Promise<FetchResult<ScheduledReportRow>> {
  return safeList<ScheduledReportRow>('/admin/analytics/scheduled-reports', 'reports', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.recipients} ${r.schedule}`),
  )
}

const STR = {
  title: 'Rejalashtirilgan hisobotlar',
  name: 'Hisobot',
  schedule: 'Jadval',
  format: 'Format',
  recipients: 'Qabul qiluvchilar',
  lastRun: 'Oxirgi ishga tushish',
  nextRun: 'Keyingi',
  status: 'Holat',
  active: 'Faol',
  paused: 'Pauza',
  failed: 'Xato',
} as const

/** A16.41 — Rejalashtirilgan hisobotlar (avto-yuboriladigan davriy eksportlar). */
export function ScheduledReports() {
  const columns = useMemo<AdminColumn<ScheduledReportRow>[]>(
    () => [
      { key: 'name', header: STR.name, type: 'text' },
      { key: 'schedule', header: STR.schedule, type: 'text' },
      {
        key: 'format',
        header: STR.format,
        type: 'enum',
        options: [
          { value: 'csv', label: 'CSV' },
          { value: 'xlsx', label: 'XLSX' },
          { value: 'pdf', label: 'PDF' },
          { value: 'json', label: 'JSON' },
        ],
      },
      { key: 'recipients', header: STR.recipients, type: 'text' },
      { key: 'last_run', header: STR.lastRun, type: 'date' },
      { key: 'next_run', header: STR.nextRun, type: 'date' },
      {
        key: 'status',
        header: STR.status,
        type: 'enum',
        options: [
          { value: 'active', label: STR.active, status: 'active' },
          { value: 'paused', label: STR.paused, status: 'suspended' },
          { value: 'failed', label: STR.failed, status: 'rejected' },
        ],
      },
    ],
    [],
  )
  const fetcher = useCallback(fetchScheduledReports, [])
  return (
    <>
      <PageHeader title={STR.title} screenId="A16.41" />
      <DataTable<ScheduledReportRow>
        screenId="A16.41"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <CalendarClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
